// L0 of the chart architecture (INS-1079): the single place Highcharts
// modules are registered and global defaults applied. Imported eagerly from
// main.tsx (so lazily-loaded routes can never render a chart before
// registration) and as a side effect by the shared chart components (so unit
// tests that mount them in isolation are covered too). Evaluates once.
//
// Core and modules come from highcharts/es-modules/masters/ — the exact
// specifier @highcharts/react imports internally — so the app and the wrapper
// share one Highcharts instance natively: the theme, modules, and Templating
// helpers registered here apply to every chart. Never use the wrapper's
// per-series/module components: they import from the bundled esm/ graph, a
// SECOND instance where none of this registration exists
// (highcharts-react#555).
//
// Theme (dark/light) application lives in the L1 Chart wrapper, per render.
import Highcharts from "highcharts/es-modules/masters/highcharts.src.js";
import { round } from "lodash-es";

import { Env } from "~/utils/Env";

// Module registrations — a relative import so it sorts (and evaluates) after
// the core master above; see the ordering note in highchartsModules.ts.
import "./highchartsModules";

// @ts-expect-error Templating exists at runtime but is still untyped in v12
Highcharts.Templating.helpers.round = (value: number) => round(value, 2);

Highcharts.setOptions({
  // Pin the pre-v12 look (INS-1079 Arc 2 decision): v12 left-aligns and
  // adaptively scales titles. Adopting the v12 look is a separate, deliberate
  // change.
  title: { align: "center", minScale: 1 },
  subtitle: { align: "center" },
  // No lang.locale pin: datetime labels follow the page language (html.lang
  // tracks i18n — see src/i18n.ts), so pt-BR users get Portuguese month
  // names. The tick format TEMPLATES stay pinned to the compact v11 style —
  // in v12 these render through Intl, so %b localizes ("Sep" / "set.").
  xAxis: {
    dateTimeLabelFormats: {
      millisecond: "%H:%M:%S.%L",
      second: "%H:%M:%S",
      minute: "%H:%M",
      hour: "%H:%M",
      day: "%e %b",
      week: "%e %b",
      month: "%b '%y",
      year: "%Y",
    },
  },
  // Highcharts animates SVG attributes in JS, which Playwright's
  // CSS-animation freeze cannot stop — disable globally under the E2E build
  // so visual snapshots are stable.
  ...(Env.VITE_E2E === "true"
    ? {
        chart: { animation: false },
        plotOptions: { series: { animation: false } },
      }
    : {}),
});

export default Highcharts;
